<template>
  <div
    class="action-menu"
    :class="`
      action-menu--${props.position}
      action-menu--${menuVerticalPosition}
    `"
  >
    <div
      ref="toggleButtonRef"
      class="action-menu__button-wrapper"
      @click.stop.prevent="toggleMenu"
    >
      <slot name="button">
        <button class="action-menu__button">
          <span class="iconfont" :class="icon"></span>
        </button>
      </slot>
    </div>

    <Transition name="fade">
      <ul
        v-if="isMenuVisible"
        ref="listRef"
        v-click-outside="hideMenu"
        class="action-menu__list"
        @click="hideMenu"
      >
        <slot></slot>
      </ul>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'

import { emitter } from '@/services/mitt'

/**
 * This component is a button that opens a dropdown menu of actions (`.action-menu__list`)
 * when clicked. The button component can be customized by either passing
 * the `icon` prop, or using the `#button` slot.
 *
 * The default slot is meant to be used with the `ActionMenuItem` component, which makes
 * it easier to handle the logic and styling of each item.
 *
 * @param position - The aligment of the dropdown list relative to the button.
 * @param icon - An iconfont icon class name that will be used as the button icon.
 *   Note: the button can be customized entirely by using the `#button` slot instead.
 */
const props = withDefaults(
  defineProps<{
    position?: 'center' | 'right'
    icon?: string
  }>(),
  {
    position: 'center',
    icon: 'imore',
  },
)

const isMenuVisible = ref(false)
const menuVerticalPosition = ref<'top' | 'bottom'>('bottom')

const toggleButtonRef = ref<null | HTMLElement>(null)
const listRef = ref<null | HTMLElement>(null)

function toggleMenu(event: MouseEvent): void {
  if (isMenuVisible.value) {
    hideMenu(event)
  } else {
    showMenu(event)
  }
}

async function showMenu(event: MouseEvent): Promise<void> {
  emitter.emit('showActionMenu')
  isMenuVisible.value = true

  // Check which position to render the menu
  // (above or below the button). Since this function
  // uses the menu's `offsetHeight` in it's logic, we
  // need to make sure the menu has rendered before running
  // the function, hence why we wait vue's `nextTick()`.
  await nextTick()
  checkMenuPosition(event)
}

function hideMenu(event: MouseEvent): void {
  // Don't hide when clicking the toggle button, as that would
  // cause the menu to never show up.
  if (event.target === toggleButtonRef.value) {
    return
  }

  isMenuVisible.value = false
}

/**
 * Check if there's enough position below the button
 * to show the action menu.
 *
 * If it does, render the menu below the button. If
 * it doesn't, render the menu above the button.
 *
 * Because this function checks the height of the menu
 * with its `offsetHeight` property, the menu needs
 * to be rendered before running it.
 */
function checkMenuPosition(event: MouseEvent): void {
  const position = event.y
  const screenHeight = window.innerHeight

  const menuHeight = listRef.value?.offsetHeight || 0
  const bufferSpace = 100

  const availableSpace = screenHeight - position
  const requiredSpace = menuHeight + bufferSpace

  if (requiredSpace >= availableSpace) {
    menuVerticalPosition.value = 'top'
  } else {
    menuVerticalPosition.value = 'bottom'
  }
}

onMounted(() => {
  // Close other instances of ActionMenu when we show this one
  emitter.on('showActionMenu', () => {
    isMenuVisible.value = false
  })

  // Close the menu when the page is scrolled
  emitter.on('appScroll', () => {
    isMenuVisible.value = false
  })
})
</script>

<style lang="scss" scoped>
.action-menu {
  --arrow-width: 6px;
  --icon-size: 24px;
  --horizontal-distance: 16px;
  --button-color: $c-black-2;
  --button-background-hover: transparent;
  position: relative;
  @media (max-width: $w-medium-s) {
    --arrow-width: 4px;
  }

  &__list {
    position: absolute;
    z-index: 10;
    background: $c-white;
    border-radius: 8px;
    border: 1px solid $c-border;
    padding: 8px;
    width: max-content;
    transition: all 0.2s;
    filter: drop-shadow(0px 1px 5px rgba(255, 255, 255, 0.1))
      drop-shadow(0px 8px 15px rgba(20, 28, 49, 0.1));
    @media (max-width: $w-medium-s) {
      padding: 4px;
    }

    &:before {
      content: '';
      position: absolute;
      width: calc(var(--arrow-width) * 2);
      height: calc(var(--arrow-width) * 2);
      background: inherit;
      border: 1px solid $c-border;
      clip-path: polygon(100% 0, 0 0, 0 100%);
      pointer-events: none;
    }
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    margin: -8px;
    border-radius: 4px;
    font-size: var(--icon-size);
    line-height: 1;
    color: var(--button-color);
    @include hover {
      background: var(--button-background-hover);
    }

    .iconfont {
      width: var(--icon-size);
      height: var(--icon-size);
    }

    @media (max-width: $w-tablet-s) {
      border-radius: 99px;
    }
  }

  &__button-wrapper {
    width: fit-content;
  }
}

.action-menu--center .action-menu {
  &__list {
    left: 50%;
    transform: translateX(-50%);

    &:before {
      left: calc(50% - var(--arrow-width));
    }
  }
}

.action-menu--right .action-menu {
  &__list {
    right: calc(var(--horizontal-distance) * -1);

    &:before {
      right: calc(var(--horizontal-distance) + var(--arrow-width));
    }
  }
}

.action-menu--top .action-menu {
  &__list {
    bottom: calc(100% + calc(var(--arrow-width) * 2));

    &:before {
      bottom: calc(var(--arrow-width) * -1);
      transform: rotate(225deg);
    }
  }
}

.action-menu--bottom .action-menu {
  &__list {
    top: calc(100% + calc(var(--arrow-width) * 2));

    &:before {
      top: calc(var(--arrow-width) * -1);
      transform: rotate(45deg);
    }
  }
}

.night-mode .action-menu {
  &__list {
    background: $c-dark-1;
    border-color: $c-dark-3;

    &:before {
      border-color: $c-dark-3;
    }
  }
}
</style>
